import React, { useState, useEffect } from "react";
import { gql, useQuery, useMutation, useApolloClient } from "@apollo/client";
import {
  Container,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
} from "@mui/material";
import { Icon } from "@iconify/react";
import Header from "../components/Headers/Header";
import CustomLoader from "../components/Loader/CustomLoader";
import useGlobalStyles from "../utils/globalStyles";

const GET_RIDER_CONFIG = gql`
  query GetRiderConfigForLS {
    getRiderConfigForLS {
      expansionRatio
      thresholdFactor
      locationUpdateTimeInSecs
      deliveryDistanceForNotificationInMts
    }
  }
`;

const UPDATE_RIDER_CONFIG = gql`
  mutation UpdateRiderConfig(
    $locationUpdateTimeInSecs: Int!
    $deliveryDistanceForNotificationInMts: Float!
    $threshold: Float
    $expansionRatio: Float
  ) {
    updateRiderConfig(
      locationUpdateTimeInSecs: $locationUpdateTimeInSecs
      deliveryDistanceForNotificationInMts: $deliveryDistanceForNotificationInMts
      threshold: $threshold
      expansionRatio: $expansionRatio
    ) {
      expansionRatio
      thresholdFactor
      locationUpdateTimeInSecs
      deliveryDistanceForNotificationInMts
    }
  }
`;

const RiderConfigPage = () => {
  const globalClasses = useGlobalStyles();
  const client = useApolloClient();

  const [expansionRatio, setExpansionRatio] = useState("");
  const [thresholdFactor, setThresholdFactor] = useState("");
  const [locationUpdateTimeInSecs, setLocationUpdateTimeInSecs] = useState("");
  const [deliveryDistanceForNotificationInMts, setDeliveryDistanceForNotificationInMts] = useState("");

  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "" });

  const [errors, setErrors] = useState({});

  const { data, loading, error: queryError, refetch } = useQuery(GET_RIDER_CONFIG, {
    onError: (err) => {
      const isForbidden =
        err && err.graphQLErrors && err.graphQLErrors.some((e) => e.extensions.code === "FORBIDDEN");

      if (isForbidden) {
        localStorage.removeItem("user-native");
        client.clearStore();
        window.location.href = "/auth/login";
      } else {
        setSnackbar({ open: true, message: err.message || "Failed to load configuration.", severity: "error" });
      }
    },
  });

  useEffect(() => {
    if (data && data.getRiderConfigForLS) {
      const config = data.getRiderConfigForLS;
      setExpansionRatio(config.expansionRatio || "");
      setThresholdFactor(config.thresholdFactor || "");
      setLocationUpdateTimeInSecs(config.locationUpdateTimeInSecs || "");
      setDeliveryDistanceForNotificationInMts(config.deliveryDistanceForNotificationInMts || "");
    }
  }, [data]);

  const [updateRiderConfig] = useMutation(UPDATE_RIDER_CONFIG, {
    onCompleted: () => {
      setSnackbar({ open: true, message: "Rider configuration updated successfully!", severity: "success" });
      refetch();
      setUpdating(false);
    },
    onError: (mutationError) => {
      setSnackbar({ open: true, message: mutationError.message || "Failed to update rider configuration.", severity: "error" });
      setUpdating(false);
    },
  });

  const handleUpdate = () => {
    const newErrors = {};
    if (isNaN(parseFloat(expansionRatio))) {
      newErrors.expansionRatio = "Please enter a valid number.";
    }
    if (isNaN(parseFloat(thresholdFactor))) {
      newErrors.thresholdFactor = "Please enter a valid number.";
    }
    if (isNaN(parseInt(locationUpdateTimeInSecs, 10))) {
      newErrors.locationUpdateTimeInSecs = "Please enter a valid integer.";
    }
    if (isNaN(parseFloat(deliveryDistanceForNotificationInMts))) {
      newErrors.deliveryDistanceForNotificationInMts = "Please enter a valid number.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setUpdating(true);
    updateRiderConfig({
      variables: {
        expansionRatio: parseFloat(expansionRatio),
        threshold: parseFloat(thresholdFactor),
        locationUpdateTimeInSecs: parseInt(locationUpdateTimeInSecs, 10),
        deliveryDistanceForNotificationInMts: parseFloat(deliveryDistanceForNotificationInMts),
      },
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return <CustomLoader />;
  }

  return (
    <>
      <Header />
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, borderRadius: "12px" }}>
          <Typography
            variant="h5"
            gutterBottom
            align="center"
            sx={{
              fontWeight: 700,
              color: "primary.main",
              mb: 3,
            }}
          >
            Rider Configuration Settings
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Expansion Ratio"
                type="number"
                value={expansionRatio}
                onChange={(e) => setExpansionRatio(e.target.value)}
                fullWidth
                variant="outlined"
                error={!!errors.expansionRatio}
                helperText={errors.expansionRatio}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover fieldset": {
                      borderColor: "primary.light",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                    },
                    "& input": { // Target the input element inside
                      color: "#000000", // Black text
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#616161",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Threshold Factor"
                type="number"
                value={thresholdFactor}
                onChange={(e) => setThresholdFactor(e.target.value)}
                fullWidth
                variant="outlined"
                error={!!errors.thresholdFactor}
                helperText={errors.thresholdFactor}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#e0e0e0" },
                    "&:hover fieldset": { borderColor: "primary.light" },
                    "&.Mui-focused fieldset": { borderColor: "primary.main" },
                    "& input": { // Target the input element inside
                      color: "#000000", // Black text
                    },
                  },
                  "& .MuiInputLabel-root": { color: "#616161" },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Location Update Time"
                type="number"
                value={locationUpdateTimeInSecs}
                onChange={(e) => setLocationUpdateTimeInSecs(e.target.value)}
                fullWidth
                variant="outlined"
                error={!!errors.locationUpdateTimeInSecs}
                helperText={errors.locationUpdateTimeInSecs}
                InputProps={{
                  endAdornment: <InputAdornment position="end">seconds</InputAdornment>,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#e0e0e0" },
                    "&:hover fieldset": { borderColor: "primary.light" },
                    "&.Mui-focused fieldset": { borderColor: "primary.main" },
                    "& input": { // Target the input element inside
                      color: "#000000", // Black text
                    },
                  },
                  "& .MuiInputLabel-root": { color: "#616161" },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Delivery Notification Distance"
                type="number"
                value={deliveryDistanceForNotificationInMts}
                onChange={(e) => setDeliveryDistanceForNotificationInMts(e.target.value)}
                fullWidth
                variant="outlined"
                error={!!errors.deliveryDistanceForNotificationInMts}
                helperText={errors.deliveryDistanceForNotificationInMts}
                InputProps={{
                  endAdornment: <InputAdornment position="end">meters</InputAdornment>,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#e0e0e0" },
                    "&:hover fieldset": { borderColor: "primary.light" },
                    "&.Mui-focused fieldset": { borderColor: "primary.main" },
                    "& input": { // Target the input element inside
                      color: "#000000", // Black text
                    },
                  },
                  "& .MuiInputLabel-root": { color: "#616161" },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={updating}
                fullWidth
                size="large"
                startIcon={updating ? <CircularProgress size={20} color="inherit" /> : <Icon icon="mdi:content-save" />}
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "1rem",
                  backgroundColor: "primary.main",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                  "&:disabled": {
                    backgroundColor: "grey.300",
                    color: "grey.500",
                  },
                }}
              >
                {updating ? "UPDATING..." : "UPDATE CONFIGURATION"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RiderConfigPage;